<script setup lang="ts">
import { useEventListener, useIntersectionObserver, useMediaQuery } from "@vueuse/core";
import { isClient } from "@vueuse/shared";
import { computed, ref, unref, useTemplateRef } from "vue";

export interface ParallaxContainerProps {
  contentScale?: number;
  enableObserverOnMount?: boolean;
  reverseAnimation?: boolean;
  // variant?: "move" | "scroll";
}

const {
  contentScale = 1.25,
  enableObserverOnMount = true,
  reverseAnimation = true,
  // variant = "scroll",
} = defineProps<ParallaxContainerProps>();

const containerRef = useTemplateRef<HTMLDivElement>("containerRef");
const percentage = ref(0);
const isIntersecting = ref(false);

// Accessibility
const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

// Styling
const animationDelay = computed(() => `${unref(percentage) * -100}s`);
const scaleOffset = computed(() => `${(contentScale - 1) * 0.5 * 100}%`);

// Active / inactive
const shouldObserve = computed(() => enableObserverOnMount && !unref(prefersReducedMotion));
const intersectionTarget = computed(() => (unref(shouldObserve) ? unref(containerRef) : null));

const updateScrollPercentage = () => {
  const container = unref(containerRef);
  if (!isClient || !container || unref(prefersReducedMotion)) {
    return;
  }

  const rect = container.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const distance = rect.height + windowHeight;
  const covered = windowHeight - rect.top;

  percentage.value = Math.min(Math.max(covered / distance, 0), 1);
};

// Scroll listener (only active when intersecting)
useEventListener(
  () => (unref(isIntersecting) && !unref(prefersReducedMotion) ? document : null),
  "scroll",
  updateScrollPercentage,
  {
    passive: true,
  },
);

useIntersectionObserver(
  intersectionTarget,
  ([entry]) => {
    isIntersecting.value = entry.isIntersecting;
    if (entry.isIntersecting) {
      updateScrollPercentage();
    }
  },
  {
    rootMargin: "10px",
    threshold: [0, 0.1, 0.9, 1.0],
  },
);

const setPercentage = (newPercentageValue: number) => {
  if (unref(prefersReducedMotion)) {
    return;
  }

  percentage.value = newPercentageValue;
};

defineExpose({ setPercentage });
</script>

<template>
  <div ref="containerRef" class="overflow-hidden">
    <div
      class="content h-full w-full"
      :class="{ 'animation-reverse': reverseAnimation }"
      :style="{
        '--animation-delay': animationDelay,
        '--scale-offset': scaleOffset,
        '--content-scale': contentScale,
      }"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped lang="postcss">
@media (prefers-reduced-motion: no-preference) {
  .content {
    animation-name: scroll-parallax-y;
    animation-duration: 100s;
    animation-fill-mode: forwards;
    animation-play-state: paused;
    animation-timing-function: ease-in;
    animation-delay: var(--animation-delay, 0s);

    &.animation-reverse {
      animation-direction: reverse;
    }
  }

  @keyframes scroll-parallax-y {
    0% {
      transform: translateY(var(--scale-offset)) scale(var(--content-scale));
    }

    50% {
      transform: translateY(0) scale(var(--content-scale));
    }

    100% {
      transform: translateY(calc(var(--scale-offset) * -1)) scale(var(--content-scale));
    }
  }
}
</style>
